import { createFileRoute } from "@tanstack/react-router"
import { PersonNameWithUserNameFallback } from "@/routes/-components/chat/ChatListForCurrentUser/PersonNameWithUserNameFallback"
import { AdminInfiniteUserSelect } from "@components/InfiniteUserSelect"
import { Page } from "@components/Page"
import { Stack, Group, ActionIcon, Highlight } from "@mantine/core"
import { useInterval } from "@mantine/hooks"
import {
  IconRefresh,
  IconPlayerPause,
  IconPlayerPlay,
} from "@tabler/icons-react"
import { useTranslation } from "react-i18next"
import {
  ResponsiveFilterGroup,
  useResponsiveFilterQueryOptions,
} from "../../-components/hideable-filter-group"
import { LogLevel, LogLevelSelect } from "../../-components/log-level"
import { getFormattedDate } from "@/utils/get-formatted-date"
import {
  InfiniteScrollingTable,
  TheasoftColumnDef,
} from "@/routes/-components/custom-tanstack-table"
import { DateTimePicker } from "@mantine/dates"
import { getPaginatedLogs, LogDto } from "@/orval/gen"
import { useDefaultInfiniteQuery } from "@/utils/useDefaultInfiniteQuery"
import { useBreakpoints } from "@/utils/use-breakpoints"
import { useUserLocalStorage } from "../../-components/use-user-local-storage"
import { getDateTime } from "@/utils/getDateTime"

export interface Search {
  globalFilter?: string
  level?: LogLevel
  userId?: number | null
  start?: string | null
  end?: string | null
}

export const Route = createFileRoute("/app/admin-settings/logging/")({
  component: LoggingTablePage,
  validateSearch: (search: Search) => search,
  beforeLoad: (ctx) => ({ getTitle: () => ctx.context.t("NavItem_Logging") }),
})

export function LoggingTablePage() {
  const search = Route.useSearch()
  const navigate = Route.useNavigate()
  const updateSearch = (input: Partial<Search>) =>
    navigate({ to: ".", search: { ...search, ...input } })
  const [showFilters, setShowFilters] = useUserLocalStorage({
    key: "logging-show-filters",
    defaultValue: false,
  })
  const queryOptions = useResponsiveFilterQueryOptions(showFilters)
  const { t } = useTranslation("app_server")
  const columns = React.useMemo<TheasoftColumnDef<LogDto>[]>(
    () => [
      { accessorKey: "id", header: "ID" },
      {
        accessorKey: "timeStamp",
        header: "Timestamp",
        cell: ({ cell }) => getFormattedDate(cell.getValue<string>(), "L LTS"),
      },
      {
        accessorKey: "level",
        header: "Level",
        cell: ({ cell }) => (
          <LogLevel withText value={cell.getValue<LogLevel>()} />
        ),
      },
      {
        accessorKey: "userId",
        header: "User",
        cell: ({ cell }) => {
          const userId = cell.getValue<number | null>()
          return userId ? (
            <PersonNameWithUserNameFallback userId={userId} />
          ) : null
        },
      },
      {
        accessorKey: "message",
        header: "Message",
        cell: ({ row }) => (
          <Highlight size="xs" highlight={search.globalFilter ?? ""}>
            {row.original.message ?? ""}
          </Highlight>
        ),
      },
      { accessorKey: "exception", header: "Exception" },
    ],
    [search]
  )
  const queryResult = useDefaultInfiniteQuery(
    getPaginatedLogs,
    (pageRequest) => ({
      ...pageRequest,
      ...search,
      start: getDateTime(search.start),
      end: getDateTime(search.end),
    }),
    queryOptions
  )
  const { start, stop, active } = useInterval(
    () => queryResult.refetch(),
    5 * 1000
  )

  const xs = useBreakpoints().xs
  const filterProps = {
    w: xs ? "100%" : 400,
    clearable: true,
    searchable: true,
  }
  return (
    <Page
      buttonGroupProps={{
        children: (
          <>
            <Group>
              <ActionIcon
                variant="transparent"
                c={"gray"}
                onClick={() => queryResult.refetch()}
              >
                <IconRefresh />
              </ActionIcon>
              {active ? (
                <ActionIcon variant="transparent" c={"gray"} onClick={stop}>
                  <IconPlayerPause />
                </ActionIcon>
              ) : (
                <ActionIcon variant="transparent" c={"gray"} onClick={start}>
                  <IconPlayerPlay />
                </ActionIcon>
              )}
            </Group>
            <ResponsiveFilterGroup.Button
              visible={showFilters}
              onChange={() => setShowFilters((v) => !v)}
              filters={{
                level: search.level,
                userId: search.userId,
                dateRange: search.start || search.end,
              }}
            />
          </>
        ),
      }}
    >
      <Stack gap="xs" h="100%">
        <ResponsiveFilterGroup
          visible={showFilters}
          setVisible={setShowFilters}
          filters={{
            level: search.level,
            userId: search.userId,
            dateRange: search.start || search.end,
          }}
          onReset={() =>
            updateSearch({
              level: undefined,
              userId: null,
              start: undefined,
              end: undefined,
            })
          }
        >
          <DateTimePicker
            {...filterProps}
            value={search.start ?? null}
            onChange={(value) =>
              updateSearch({
                start: value,
              })
            }
            placeholder={t("Start")}
            w={200}
            maxDate={new Date()}
          />
          <DateTimePicker
            {...filterProps}
            value={search.end ?? null}
            onChange={(value) =>
              updateSearch({
                end: value,
              })
            }
            placeholder={t("End")}
            w={200}
            maxDate={new Date()}
          />
          <LogLevelSelect
            {...filterProps}
            value={search.level}
            onChange={(value) =>
              updateSearch({
                level: (value ?? undefined) as LogLevel | undefined,
              })
            }
          />
          <AdminInfiniteUserSelect
            {...filterProps}
            styles={{ dropdown: { minWidth: 200 } }}
            placeholder="User"
            onSelectionChange={(v) =>
              updateSearch({
                userId: v != null ? parseInt(v) : null,
              })
            }
            value={search.userId?.toString()}
            filterOptions={{ filterInactive: false, userTypeFilter: "Users" }}
          />
        </ResponsiveFilterGroup>
        <InfiniteScrollingTable
          id="logging-table"
          infiniteQueryResult={queryResult}
          columnDefinitions={columns}
          defaultColumnVisibility={{
            id: false,
            exception: false,
            logEvent: false,
          }}
          getRowProps={(row) => ({
            onClick: () =>
              navigate({
                to: "/app/admin-settings/logging/$id",
                params: { id: row.original.id.toString() },
              }),
          })}
        />
      </Stack>
    </Page>
  )
}
